
import { CaseDetails, CaseField, DataType } from "@/types"
import { CaseColumn, caseColumns } from "@/types/caseColumns"

import { FieldDisplay, FieldType } from "@/types/mergeFieldTypes"

import User from "@/types/user"
import cloneDeep from "lodash/cloneDeep"

import _ from "lodash"
import CaseFile from "@/types/caseFile"

class FieldDisplayMapping {
	public getGlobalColumns (caseDetails: CaseDetails | null, caseFiles: CaseFile[]): CaseColumn[] {
		if (!caseDetails) {
			return []
		}

		const hideUsers = Object.keys(caseDetails.fields)
			.some(fieldId => caseDetails.fields[fieldId].field.dataType === "user-role")

		const globalColumns = caseColumns.filter(c =>
			c.key !== "caseNumber" &&
			c.key !== "title" &&
			c.key !== "templateId" &&
			c.key !== "lastUpdated" &&
			c.key !== "created" &&
			c.key !== "openForDays" &&
			c.key !== "isClosed" &&
			c.key !== "closed" &&
			(!hideUsers || (c.key !== "users" && c.key !== "assigneeId")))

		globalColumns.forEach(c => {
			c.value = _.get(caseDetails, c.key)
			if (c.displayKey) {
				c.valueLabel = _.get(caseDetails, c.displayKey)
			}
		})

		globalColumns.push({
			key: "attachments",
			label: "Attachments",
			dataType: "list-multiple",
			fieldDataType: DataType.arrayMultiple,
			listLabel: "filename",
			value: caseFiles.filter(c => !c.fieldId),
			dynamicField: false
		})

		const assignee = globalColumns.find(g => g.key === "assigneeId")
		const users = globalColumns.find(g => g.key === "users")
		if (assignee && users) {
			assignee.value = users.value!.find((u: any) => u.id === caseDetails.assigneeId)
			users.value = users.value!.filter((u: any) => u.id !== caseDetails.assigneeId)
		}

		return globalColumns
	}

	public getGlobalDisplayFields (caseDetails: CaseDetails | null, caseFiles: CaseFile[]): FieldDisplay[] {
		let caseDetailsCopy = cloneDeep(caseDetails)
		let globalColumns = this.getGlobalColumns(caseDetailsCopy, caseFiles) || []

		return globalColumns.map((c: CaseColumn) => {
			let value: any
			if (c.dataType === "list-multiple" || c.dataType === "list-data") { // Areas, Tags & Teams
				value = (c.value && Array.isArray(c.value) && c.listLabel
					? c.value.map(item => { return { label: item[c.listLabel!], id: item.id } })
					: [])
			} else if (c.dataType === "list") { // Severity & Status
				value = c.valueLabel || c.value
			} else {
				value = c.value
			}

			let fieldDisplay: FieldDisplay = {
				id: c.key,
				dataType: c.dataType,
				title: c.label,
				value: value,
				valueObject: c.valueObject,
				listItemType: c.listItemType || null,
				hasViewPermission: true,
				fieldType: FieldType.global
			}

			return fieldDisplay
		})
	}

	public getDisplayFields (caseDetails: CaseDetails | null, caseFiles: CaseFile[], user: User): FieldDisplay[] {
		if (!caseDetails) {
			return []
		}

		let allFields: CaseField[] = []

		if (caseDetails.fields) {
			Object.keys(caseDetails.fields).forEach(fieldId => {
				const field = caseDetails.fields[fieldId]
				if (field &&
					field.field.dataType !== "calculated" &&
					field.field.dataType !== "case-number") {
					allFields.push(field)
				}
			})
		}

		return allFields.map((c: CaseField) => {
			let fieldDisplay: FieldDisplay = {
				id: c.field.id!,
				dataType: c.field.dataType!,
				title: c.field.title || "",
				value: c.field.allowFiles ? this.getCaseFilename(c, caseFiles) : c.field.dataType === "list" ? c.label : c.value,
				valueObject: c.valueObject,
				listItemType: null,
				hasViewPermission: this.hasViewPermission(c, caseDetails, user),
				fieldType: FieldType.regular
			}

			return fieldDisplay
		})
	}

	private getCaseFilename (caseField: CaseField, caseFiles: CaseFile[]) : string {
		let caseFile = caseFiles.find(c => c.fieldId === caseField.field.id!)
		return caseFile!.filename
	}

	public hasViewPermission (field: CaseField, caseDetails: CaseDetails, user: User) : boolean {
		if (!field.permissions && !field.field.permissions) {
			return true
		}

		var permissions = field.permissions ? caseDetails.isCase ? field.permissions.case : field.permissions.record : null
		if (!permissions) {
			permissions = caseDetails.isCase ? field.field.permissions.case : field.field.permissions.record
		}

		return !permissions.view || permissions.view.length === 0 ||
			(user.teams && permissions.view.some(team => user.teams.find((myTeam: any) => myTeam.id === team.id)))
	}
}

const fieldDisplayMapping = new FieldDisplayMapping()
export default fieldDisplayMapping
